import { format } from 'date-fns';
import { CalendarClock, CalendarX2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
// Deep import, NOT the `@/features/organization` barrel: the barrel pulls the
// router in.
import { useActiveOrganization } from '@/features/organization/api/get-active-organization';
import { zonedEvent } from '@/lib/timezone';
import { cn } from '@/lib/utils';
import {
  type AppointmentStatus,
  type AppointmentWithRelations,
  appointmentStatusLabels,
} from '@borradh-workspace/api-client/types';
import { activeAppointmentStatuses } from '@borradh-workspace/labels';
import { useClientAppointments } from '../api';

// A cancelled or no-show booking in the future is not a visit the client still
// has coming up, so it belongs with the history, not under Upcoming.
const ACTIVE_STATUSES = new Set<string>(activeAppointmentStatuses);

const statusBadgeClass: Record<AppointmentStatus, string> = {
  booked:
    'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  confirmed:
    'border-transparent bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300',
  arrived:
    'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  started:
    'border-transparent bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300',
  completed:
    'border-transparent bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
  no_show:
    'border-transparent bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  cancelled: 'border-transparent bg-muted text-muted-foreground',
  // Dashed, not solid: a hold is provisional and releases itself if it is never
  // confirmed, so it should not read as a settled booking at a glance.
  held: 'border-dashed border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
};

function AppointmentStatusBadge({ status }: { status: AppointmentStatus }) {
  return (
    <Badge className={cn('text-xs', statusBadgeClass[status])}>
      {appointmentStatusLabels[status]}
    </Badge>
  );
}

function AppointmentRow({
  appointment,
  timeZone,
}: {
  appointment: AppointmentWithRelations;
  /** The clinic's timezone. */
  timeZone: string;
}) {
  // Clinic time, not the device's: the same booking must read the same on
  // every phone.
  const start = zonedEvent(appointment.startDate, timeZone);
  const end = zonedEvent(appointment.endDate, timeZone);
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
      <div className="min-w-0">
        <p className="truncate font-medium">{appointment.title}</p>
        <p className="text-sm text-muted-foreground">
          {format(start, 'EEE d MMM yyyy')} · {format(start, 'HH:mm')}–
          {format(end, 'HH:mm')}
        </p>
      </div>
      <AppointmentStatusBadge status={appointment.status} />
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  appointments,
  timeZone,
}: {
  icon: typeof CalendarClock;
  title: string;
  appointments: AppointmentWithRelations[];
  timeZone: string;
}) {
  if (appointments.length === 0) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon className="size-4" />
        {title} ({appointments.length})
      </div>
      <div className="space-y-2">
        {appointments.map((a) => (
          <AppointmentRow key={a.id} appointment={a} timeZone={timeZone} />
        ))}
      </div>
    </div>
  );
}

export function ClientAppointmentsTab({ leadId }: { leadId: string }) {
  const { appointments, isLoading, isError } = useClientAppointments(leadId);
  const { data: organization } = useActiveOrganization();
  // Clinic-timezone times, and an Upcoming list that leaves out cancelled
  // visits.
  const timeZone = organization?.timezone ?? 'UTC';

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((n) => (
          <Skeleton key={n} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Failed to load appointments.
      </p>
    );
  }

  if (appointments.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CalendarX2 />
          </EmptyMedia>
          <EmptyTitle>No appointments yet</EmptyTitle>
          <EmptyDescription>
            Appointments booked with this client will appear here.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const now = Date.now();
  const isUpcoming = (a: AppointmentWithRelations) =>
    ACTIVE_STATUSES.has(a.status) && new Date(a.startDate).getTime() >= now;
  const upcoming = appointments
    .filter(isUpcoming)
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
  const past = appointments
    .filter((a) => !isUpcoming(a))
    .sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        Times shown in {timeZone.replace(/_/g, ' ')}
      </p>
      <Section
        icon={CalendarClock}
        title="Upcoming"
        appointments={upcoming}
        timeZone={timeZone}
      />
      <Section
        icon={CalendarX2}
        title="Past & cancelled"
        appointments={past}
        timeZone={timeZone}
      />
    </div>
  );
}
